import { Button } from '@heidi/design-system/button';
import { PopoverMenuItem } from '@heidi/design-system/popover-menu-item';
import { Separator } from '@heidi/design-system/separator';
import { Skeleton } from '@heidi/design-system/skeleton';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { isValid, parseISO } from 'date-fns';
import { ExternalLink, Loader2, RefreshCw, Unlink } from 'lucide-react';
import { useIntl } from 'react-intl';

import { useQueryPatientRecap } from '../../../data';
import { calculateAge } from '../../patient-profile/identity';
import { useIdentityEditorIntl } from '../../patient-profile/useIdentityEditorIntl';

export interface LinkedPatientPopoverProps {
  /** Patient profile id — required to fetch recap. */
  patientProfileId: string;
  /** ISO date string (`birth_date`) — optional. */
  dob: string | null;
  /** Gender string from the profile — optional. */
  gender: string | null;
  /** Opens the patient profile drawer / page. */
  onViewProfile: () => void;
  /**
   * Unlinks the current session from this patient. Omit to hide the
   * "Unlink from session" action entirely — used for EHR-sourced patients,
   * which must not be unlinked from a session (the next EHR pull would
   * recreate them as a new patient). Deletion from `/patients` is unaffected.
   */
  onUnlink?: () => void;
  /**
   * The app's resolved auth state (cohort-C), gating the recap read exactly as
   * the app delegate this surface used to call did. Same shape as
   * `PrechartInstructionsModal`.
   */
  isAuthenticated?: boolean;
}

export function LinkedPatientPopover({
  patientProfileId,
  dob,
  gender,
  onViewProfile,
  onUnlink,
  isAuthenticated = true,
}: LinkedPatientPopoverProps) {
  const intl = useIntl();
  const identityIntl = useIdentityEditorIntl();
  const { data, isLoading, isError, refetch, isFetching } = useQueryPatientRecap(
    patientProfileId,
    isAuthenticated
  );

  const age = calculateAge(dob);
  const dobLabel = dob ? identityIntl.formatDob(dob) : null;
  // 'unknown' is a real stored gender value but uninformative on its own here.
  const formattedGender =
    gender && gender !== 'unknown' ? identityIntl.formatGender(gender) : null;
  const demographicLine = [
    dobLabel && age !== null ? `${dobLabel} (${age})` : dobLabel,
    formattedGender,
  ]
    .filter(Boolean)
    .join(' · ');

  const recap = data?.recap;
  const recapError = isError || Boolean(data?.error);
  const sourceDate = recap ? parseISO(recap.source_session_date) : null;
  const sourceDateLabel =
    sourceDate && isValid(sourceDate)
      ? intl.formatDate(sourceDate, { day: 'numeric', month: 'short', year: 'numeric' })
      : null;

  const lastSessionLabel = intl.formatMessage({
    defaultMessage: 'Last session',
    id: 'PU3Ok9LSSf',
  });

  return (
    <div className="flex flex-col gap-2">
      {demographicLine && (
        <TypographyCaption className="text-text-secondary">
          {demographicLine}
        </TypographyCaption>
      )}

      <RecapSection
        recap={recap}
        sourceDateLabel={sourceDateLabel}
        eyebrowLabel={lastSessionLabel}
        isLoading={isLoading}
        isError={recapError}
        isFetching={isFetching}
        onRetry={() => void refetch()}
      />

      <div className="-mx-1 mt-1 flex flex-col">
        <Separator className="mx-1" />
        <PopoverMenuItem
          icon={<ExternalLink className="size-4" />}
          onClick={onViewProfile}
          className="mt-2"
        >
          {intl.formatMessage({ defaultMessage: 'View profile', id: 'yGGPTlHCf9' })}
        </PopoverMenuItem>
        {onUnlink && (
          <PopoverMenuItem icon={<Unlink className="size-4" />} onClick={onUnlink}>
            {intl.formatMessage({
              defaultMessage: 'Unlink from session',
              id: 'f0HJQ0ZGhu',
            })}
          </PopoverMenuItem>
        )}
      </div>
    </div>
  );
}

interface RecapSectionProps {
  recap: { blurb: string } | null | undefined;
  sourceDateLabel: string | null;
  eyebrowLabel: string;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}

function RecapSection({
  recap,
  sourceDateLabel,
  eyebrowLabel,
  isLoading,
  isError,
  isFetching,
  onRetry,
}: RecapSectionProps) {
  const intl = useIntl();
  if (isLoading) {
    return (
      <section
        aria-busy="true"
        aria-label={eyebrowLabel}
        className="flex flex-col gap-1.5"
      >
        <Eyebrow label={eyebrowLabel} loading />
        <div className="flex flex-col gap-1.5" aria-hidden="true">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section aria-label={eyebrowLabel} className="flex flex-col gap-1.5">
        <Eyebrow label={eyebrowLabel} />
        <div className="flex items-center gap-2">
          <TypographyP2 className="text-text-secondary">
            {intl.formatMessage({
              defaultMessage: "Couldn't generate summary.",
              id: 'Gy9Sk4+PV4',
            })}
          </TypographyP2>
          <Button
            variant="ghost"
            size="xs"
            onClick={onRetry}
            disabled={isFetching}
            aria-label={intl.formatMessage({
              defaultMessage: 'Retry summary generation',
              id: 'CVBCFmjbup',
            })}
          >
            <RefreshCw
              className={
                isFetching ? 'animate-spin motion-reduce:animate-none' : undefined
              }
            />
            {intl.formatMessage({ defaultMessage: 'Retry', id: '62nsdyMyIO' })}
          </Button>
        </div>
      </section>
    );
  }

  if (!recap) {
    return (
      <section aria-label={eyebrowLabel} className="flex flex-col gap-1.5">
        <Eyebrow label={eyebrowLabel} />
        <TypographyP2 className="text-text-tertiary">
          {intl.formatMessage({
            defaultMessage: 'No previous session yet.',
            id: 'gyee04F8b7',
          })}
        </TypographyP2>
      </section>
    );
  }

  return (
    <section aria-label={eyebrowLabel} className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-1.5">
        <Eyebrow label={eyebrowLabel} />
        {sourceDateLabel && (
          <>
            <TypographyCaption className="text-text-tertiary" aria-hidden="true">
              ·
            </TypographyCaption>
            <TypographyCaption className="text-text-secondary">
              {sourceDateLabel}
            </TypographyCaption>
          </>
        )}
      </div>
      <TypographyP2
        className={cn(
          'whitespace-pre-line text-pretty text-text-primary',
          'line-clamp-3'
        )}
      >
        {recap.blurb}
      </TypographyP2>
    </section>
  );
}

interface EyebrowProps {
  label: string;
  loading?: boolean;
}

function Eyebrow({ label, loading = false }: EyebrowProps) {
  return (
    <div className="flex items-center gap-1.5 text-text-secondary">
      <TypographyCaptionBold>{label}</TypographyCaptionBold>
      {loading && (
        <Loader2
          className="size-3 animate-spin text-text-tertiary motion-reduce:animate-none"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
